import type { ReviewFormDto } from '@flexbyt/shared';
import { readableTextOn } from '../../lib/contrast.js';
import { SourceGlyph, cx } from '@flexbyt/widget-preview';
import type {
    ButtonHTMLAttributes,
    InputHTMLAttributes,
    ReactNode,
    TextareaHTMLAttributes,
} from 'react';

/**
 * The pieces the four screens of the hosted form are built from.
 *
 * ---------------------------------------------------------------------------
 * The same typography as the editor's preview
 * ---------------------------------------------------------------------------
 * `FormPreview` in the dashboard draws a picture of this page so a business can
 * see what it is sending people. The two must not drift, so the parts that
 * carry the design — the 25px/35px title, the `- ` bullet convention, the left
 * alignment, the primary-coloured button — are written the same way here.
 *
 * They are not literally the same component, and deliberately so. That one is a
 * still image: its fields are `<span>`s, its buttons do nothing, its stars are
 * decoration. This one is the form. Sharing a component between "a picture of a
 * control" and "a control" means one of the two is compromised, and it is
 * always the real one.
 */

/**
 * 25px / 35px, -1px tracking, 700 — the legacy screen title exactly.
 *
 * Takes an `id` and is focusable, because it is where focus goes when the form
 * moves between screens. On the heading itself rather than on a wrapper around
 * it: a screen reader announces the element that receives focus, and "review
 * screen, heading, Keep it short and sweet" is the announcement worth making.
 * A focused `<div>` wrapping it announces a group with no name.
 *
 * `tabIndex={-1}` makes it focusable programmatically without putting it in the
 * tab order — it is a destination, not a control.
 */
export function Title({
  id,
  children,
  onColour,
}: {
  id?: string;
  children: string;
  /** Text colour on the brand-coloured first screen: bigger, and that colour. */
  onColour?: string;
}) {
  return (
    <h1
      id={id}
      tabIndex={id ? -1 : undefined}
      className={cx(
        'text-left font-bold focus:outline-none',
        onColour
          ? 'text-[32px] leading-[38px] tracking-[-1.2px] text-balance'
          : 'text-[25px] leading-[35px] tracking-[-1px] text-[#55595f]'
      )}
      style={onColour ? { color: onColour } : undefined}
    >
      {children}
    </h1>
  );
}

/**
 * The business's own copy, with the legacy list convention.
 *
 * Lines beginning `- ` become bullets. It is not Markdown and should not become
 * Markdown — the legacy editor is a plain textarea, the only thing anybody has
 * ever typed into it is a dash, and a full parser here would mean a business
 * that writes "50% off *this week*" discovers it has invented emphasis.
 */
export function Body({ children, onColour }: { children: string; onColour?: string }) {
  const lines = children.split('\n');
  const bullets = lines.filter((line) => line.trim().startsWith('- '));
  const prose = lines.filter((line) => !line.trim().startsWith('- ')).join('\n');

  return (
    <div
      className={cx('mt-2.5 text-left text-caption leading-relaxed', !onColour && 'text-[#55595f]')}
      style={onColour ? { color: onColour, opacity: 0.85 } : undefined}
    >
      {prose.trim() && <p className="whitespace-pre-line">{prose.trim()}</p>}
      {bullets.length > 0 && (
        <ul className="mt-[15px] mb-5 list-disc pl-5">
          {bullets.map((line) => (
            <li key={line}>{line.replace(/^\s*-\s*/, '')}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The form's own button, in the business's colour.
 *
 * White text on an arbitrary customer-chosen background is a contrast risk, and
 * the honest fix is not available here — the colour is theirs. What is done
 * instead is to never let the colour be the only signal: this is a real button
 * with a real label, and the focus ring is the site's accent rather than a tint
 * of theirs, so it stays visible against any choice they make.
 */
export function PrimaryButton({
  color,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { color: string }) {
  /*
    The one thing to press on each screen, so it is the boldest thing on it:
    tall, heavy type, a glow in its own colour, and an arrow that leans
    forward on hover. It sinks a little when pressed, which is most of what
    makes a button feel alive under a thumb.
  */
  return (
    <button
      {...props}
      className={cx(
        'group mt-7 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-4',
        'text-body font-bold tracking-[-0.01em]',
        'transition-[transform,filter,box-shadow,opacity] duration-[var(--duration-fast)] ease-out',
        'hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 active:scale-[0.98]',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600'
      )}
      style={{
        backgroundColor: color,
        color: readableTextOn(color),
        boxShadow: `0 10px 24px -10px ${color}, 0 2px 4px rgb(0 0 0 / 0.08)`,
      }}
    >
      {children}
      <svg
        viewBox="0 0 16 16"
        aria-hidden
        className="size-4 transition-transform duration-[var(--duration-fast)] group-hover:translate-x-1 group-disabled:translate-x-0"
        fill="none"
      >
        <path d="M3 8h10m0 0L9 4m4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/**
 * One screen of the form.
 *
 * Hidden rather than unmounted, which is how the legacy form did it and is
 * worth keeping: a live input holds its own scroll position, selection and IME
 * state, and going back to fix a typo should not lose any of them. `hidden`
 * rather than `display:none` in a class, because it also takes the screen out
 * of the accessibility tree and out of the tab order — a form whose hidden
 * screens are still tabbable is one where the focus vanishes off-screen.
 */
export function Screen({
  visible,
  children,
}: {
  visible: boolean;
  children: ReactNode;
}) {
  /*
    On a phone the visible screen stretches to the bottom of the display, so
    a full-colour screen reaches the edge. Only while visible: a `flex` class
    on a hidden element would fight the `hidden` attribute.
  */
  return (
    <div hidden={!visible} className={visible ? 'max-sm:flex max-sm:flex-1 max-sm:flex-col' : undefined}>
      {children}
    </div>
  );
}

/**
 * Send them to Google instead.
 *
 * A business that has connected an outside source can point reviewers at it
 * rather than collecting the review here. It gives the review away, and it is
 * usually the right offer: a Google rating is worth more to a local business
 * than a testimonial they host themselves.
 *
 * Real links, not `window.open`. The legacy buttons opened windows on click,
 * which pop-up blockers stop, middle-click cannot use, and a screen reader
 * announces as a button rather than as somewhere to go. `rel="noopener"`
 * because these are addresses the business supplied.
 */
export function ExternalSites({
  sites,
  prompt,
}: {
  sites: NonNullable<ReviewFormDto['welcomeScreen']['externalSites']>;
  prompt: string;
}) {
  return (
    <>
      <p className="mt-5 text-caption font-medium text-[#55595f]">{prompt}</p>
      <ul className="mt-2 space-y-2">
        {sites.map((site) => (
          <li key={site.id}>
            <a
              href={site.url}
              target="_blank"
              rel="noopener noreferrer"
              /*
                A plain hairline, drawn as a border rather than a ring.
                Tailwind builds `ring-1` out of `box-shadow`, so every one of
                these links reported a shadow — which is not what a 1px edge
                should be.
              */
              className={cx(
                'flex items-center justify-center gap-2 rounded-lg bg-white/80 px-4 py-2.5',
                'text-caption font-medium text-ink-800 border border-black/10',
                'transition-colors duration-[var(--duration-fast)] hover:bg-white',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600'
              )}
            >
              {/*
                Draws nothing for a source with no mark — `SourceGlyph` returns
                null rather than a placeholder, so the label simply centres
                itself and no guard is needed here.
              */}
              <SourceGlyph source={site.source} className="h-[26px] w-auto" />
              {site.label}
            </a>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * A labelled input, in the form's own style.
 *
 * Not the site's `Field`: that is built for the marketing pages and carries
 * their ink palette, and these sit on a background the business chose. The
 * parts that matter — the label association, the described-by wiring, the
 * error announced with `role="alert"` — are the same.
 */
export function FormField({
  label,
  as = 'input',
  error,
  hint,
  id,
  ...props
}: {
  label: string;
  as?: 'input' | 'textarea';
  error?: string | null;
  hint?: string;
  id: string;
} & InputHTMLAttributes<HTMLInputElement> &
  TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const Control = as;

  return (
    <div className="mt-5 text-left">
      <label htmlFor={id} className="text-caption font-semibold text-ink-900">
        {label}
      </label>
      {/*
        As bold as the button below it: tall, round, large type on a soft
        fill, and a thick ring in the business's own colour when it has focus
        (`--rj-brand`, set on the form) — so the field being typed in is
        unmistakable, and in their colours rather than ours.
      */}
      <Control
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={cx(error && errorId, hint && hintId).trim() || undefined}
        className={cx(
          'mt-2 block w-full rounded-xl bg-[#f3f3f5] px-4 py-3.5 text-body text-ink-950',
          'placeholder:text-ink-400 focus:outline-none',
          'transition-[background-color,box-shadow,transform] duration-[var(--duration-fast)] ease-out',
          'hover:bg-[#ececef] focus:-translate-y-px focus:bg-white',
          'focus:shadow-[0_0_0_3px_var(--rj-brand),0_12px_28px_-14px_var(--rj-brand)]',
          as === 'textarea' && 'min-h-[8.5rem] resize-y',
          error && 'shadow-[0_0_0_2px_var(--color-critical-600)] focus:shadow-[0_0_0_3px_var(--color-critical-600)]'
        )}
        {...(props as object)}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1 text-micro text-ink-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-micro text-critical-700">
          {error}
        </p>
      )}
    </div>
  );
}
